import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../../types';
import { useTheme } from '../../ThemeContext';
import { apiGet } from '../../utils/apiClient';

interface JobItem {
  id: string;
  pipeline_id: string;
  pipeline_name?: string;
  pipeline_version?: string;
  status: string;
  started_at?: string;
  finished_at?: string;
}

interface PipelineLite {
  pipeline_id: string;
  name: string;
}

export default function JobsPage() {
  const { theme } = useTheme();
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);

  // Filters
  const [pipelines, setPipelines] = useState<PipelineLite[]>([]);
  const [pipelineId, setPipelineId] = useState<string>('');
  const [jobId, setJobId] = useState<string>('');
  const [version, setVersion] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [startedFrom, setStartedFrom] = useState<string>(''); // datetime-local value
  const [startedTo, setStartedTo] = useState<string>(''); // datetime-local value
  const [loading, setLoading] = useState<boolean>(false);

  const fetchJobs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      
      // Pagination parameters
      const skip = (page - 1) * pageSize;
      params.set('skip', skip.toString());
      params.set('limit', pageSize.toString());
      
      // Filter parameters
      if (status) params.set('status', status);
      if (pipelineId) params.set('pipeline_id', pipelineId);
      if (jobId) params.set('job_id', jobId.trim());
      if (version) params.set('version', version.trim());
      if (startedFrom) {
        const iso = new Date(startedFrom).toISOString();
        params.set('started_from', iso);
      }
      if (startedTo) {
        const iso = new Date(startedTo).toISOString();
        params.set('started_to', iso);
      }
      
      const qs = params.toString();
      const endpoint = `/api/jobs${qs ? `?${qs}` : ''}`;
      const res = await apiGet(endpoint);
      if (!res.ok) throw new Error('Jobs endpoint not available');
      const data = await res.json();
      
      // Handle both old array format and new paginated format
      if (Array.isArray(data)) {
        setJobs(data);
        setTotal(data.length);
      } else {
        setJobs(data.items ?? []);
        setTotal(data.total ?? 0);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadPipelines = async () => {
    try {
      const res = await apiGet('/api/pipelines');
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        setPipelines(
          data.map((p: any) => ({ pipeline_id: p.pipeline_id, name: p.name }))
        );
      }
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    loadPipelines();
  }, []);

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const clearFilters = () => {
    setPipelineId('');
    setJobId('');
    setVersion('');
    setStatus('');
    setStartedFrom('');
    setStartedTo('');
    setPage(1);
  };

  const applyFilters = () => {
    setPage(1);
    fetchJobs();
  };

  const statusOptions = useMemo(() => ['queued', 'running', 'completed', 'failed'], []);

  const fmt = (s?: string) => (s ? new Date(s).toLocaleString() : '-');

  return (
    <div>
      <h2 style={{ marginTop: 0, color: theme.text }}>Jobs</h2>
      <p style={{ color: theme.textSecondary, marginTop: 6 }}>Execution history for all pipelines</p>

      {error && (
        <div style={{ background: theme.errorLight, color: theme.errorDark, border: `1px solid ${theme.error}`, padding: 12, borderRadius: 8, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Filters bar */}
      <div style={{ background: theme.cardBackground, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 12, marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 0.8fr 1fr 1.2fr 1.2fr 0.8fr', gap: 8, alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: theme.textSecondary }}>Pipeline</label>
            <select value={pipelineId} onChange={e => setPipelineId(e.target.value)} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }}>
              <option value="">All</option>
              {pipelines.map(p => (
                <option key={p.pipeline_id} value={p.pipeline_id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: theme.textSecondary }}>Job ID</label>
            <input value={jobId} onChange={e => setJobId(e.target.value)} placeholder="uuid" style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: theme.textSecondary }}>Version</label>
            <input value={version} onChange={e => setVersion(e.target.value)} placeholder="e.g., 1.2.0" style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: theme.textSecondary }}>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }}>
              <option value="">All</option>
              {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: theme.textSecondary }}>Started From</label>
            <input type="datetime-local" value={startedFrom} onChange={e => setStartedFrom(e.target.value)} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: theme.textSecondary }}>Started To</label>
            <input type="datetime-local" value={startedTo} onChange={e => setStartedTo(e.target.value)} style={{ width: '100%', padding: 8, border: `1px solid ${theme.inputBorder}`, borderRadius: 6, background: theme.inputBackground, color: theme.text }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={applyFilters} style={{ flex: 1, padding: '8px 10px', border: 'none', background: theme.buttonBackground, color: theme.buttonText, borderRadius: 6, cursor: 'pointer' }} disabled={loading}>
              {loading ? 'Loading…' : 'Apply'}
            </button>
            <button onClick={() => { clearFilters(); }} style={{ flex: 1, padding: '8px 10px', border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.text, borderRadius: 6, cursor: 'pointer' }}>
              Clear
            </button>
          </div>
        </div>
      </div>

      <div style={{ background: theme.cardBackground, borderRadius: 12, padding: 16, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px 120px 200px 200px 120px', gap: 8, fontWeight: 700, color: theme.textSecondary, paddingBottom: 8, borderBottom: `1px solid ${theme.border}` }}>
          <div>Pipeline</div>
          <div>Job ID</div>
          <div>Version</div>
          <div>Status</div>
          <div>Started</div>
          <div>Finished</div>
          <div>Actions</div>
        </div>
        {jobs.map(j => (
          <div key={j.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px 120px 200px 200px 120px', gap: 8, padding: '10px 0', borderBottom: `1px solid ${theme.border}`, color: theme.text }}>
            <div style={{ fontWeight: 600 }}>{j.pipeline_name || j.pipeline_id}</div>
            <div style={{ wordBreak: 'break-all' }}>{j.id}</div>
            <div>{j.pipeline_version ? `v${j.pipeline_version}` : '-'}</div>
            <div><span style={{ padding: '2px 8px', background: theme.hoverBackground, borderRadius: 12 }}>{j.status}</span></div>
            <div>{fmt(j.started_at)}</div>
            <div>{fmt(j.finished_at)}</div>
            <div>
              <a href={`/jobs/${j.id}`} style={{ padding: '6px 10px', background: theme.buttonBackground, color: theme.buttonText, border: 'none', borderRadius: 6, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' }}>
                View Details
              </a>
            </div>
          </div>
        ))}
        {jobs.length === 0 && !error && (
          <div style={{ color: theme.textSecondary, padding: '14px 0' }}>{loading ? 'Loading…' : 'No jobs found.'}</div>
        )}
      </div>

      {/* Pagination Controls */}
      {total > 0 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginTop: 16,
          padding: '12px 16px',
          background: theme.cardBackground,
          borderRadius: 8,
          border: `1px solid ${theme.border}`
        }}>
          {/* Page Info */}
          <div style={{ color: theme.text, fontSize: 14 }}>
            Showing {Math.min((page - 1) * pageSize + 1, total)} - {Math.min(page * pageSize, total)} of {total} jobs
          </div>

          {/* Page Controls */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1 || loading}
              style={{
                padding: '6px 12px',
                background: page === 1 ? theme.background : theme.buttonBackground,
                color: page === 1 ? theme.textSecondary : theme.buttonText,
                border: 'none',
                borderRadius: 6,
                cursor: page === 1 || loading ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Previous
            </button>

            <div style={{ color: theme.text, fontSize: 14, padding: '0 8px' }}>
              Page {page} of {Math.ceil(total / pageSize)}
            </div>

            <button
              onClick={() => setPage(Math.min(Math.ceil(total / pageSize), page + 1))}
              disabled={page >= Math.ceil(total / pageSize) || loading}
              style={{
                padding: '6px 12px',
                background: page >= Math.ceil(total / pageSize) ? theme.background : theme.buttonBackground,
                color: page >= Math.ceil(total / pageSize) ? theme.textSecondary : theme.buttonText,
                border: 'none',
                borderRadius: 6,
                cursor: page >= Math.ceil(total / pageSize) || loading ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Next
            </button>

            {/* Page Size Selector */}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              style={{
                marginLeft: 16,
                padding: '6px 8px',
                border: `1px solid ${theme.border}`,
                borderRadius: 6,
                background: theme.inputBackground,
                color: theme.text,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={200}>200 per page</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
